import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, Search, ChevronRight } from 'lucide-react';
import Header from './components/Header2';
import Footer from './components/Footer2';

const Orders = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    const orders = [
        {
            id: 'RANG-2026-001',
            date: '2026-03-10',
            status: 'delivered',
            total: 12999,
            items: [
                { name: 'Royal Banarasi Saree', quantity: 1, image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=200' },
            ],
        },
        {
            id: 'RANG-2026-002',
            date: '2026-03-14',
            status: 'shipped',
            total: 17998,
            items: [
                { name: 'Designer Anarkali Kurta', quantity: 2, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=200' },
            ],
        },
        {
            id: 'RANG-2026-003',
            date: '2026-03-18',
            status: 'processing',
            total: 24500,
            items: [
                { name: 'Silk Embroidered Lehenga', quantity: 1, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=200' },
            ],
        },
    ];

    const statusConfig = {
        delivered: { icon: CheckCircle, color: '#22C55E', label: 'Delivered' },
        shipped: { icon: Truck, color: '#FF6B35', label: 'Shipped' },
        processing: { icon: Clock, color: '#EAB308', label: 'Processing' },
    };

    const filteredOrders = orders
        .filter(o => filterStatus === 'all' || o.status === filterStatus)
        .filter(o => !searchTerm || o.id.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="w-full min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
            <Header />

            <main className="pt-[90px] md:pt-[110px] pb-16">
                <div className="max-w-[1000px] mx-auto px-4 md:px-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-white mb-8 font-['Montserrat'] uppercase">
                        My Orders
                    </h1>

                    {/* Search & Filter */}
                    <div className="flex flex-col md:flex-row gap-4 mb-8">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by order ID..."
                                className="w-full pl-10 pr-4 py-3 text-sm bg-[#1A1A1A] border border-[#2A2A2A] text-white placeholder-[#525252] focus:outline-none focus:border-[#FF6B35] transition-colors font-['Montserrat']"
                            />
                        </div>
                        <div className="flex gap-2">
                            {['all', 'processing', 'shipped', 'delivered'].map((status) => (
                                <button
                                    key={status}
                                    onClick={() => setFilterStatus(status)}
                                    className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all duration-300 font-['Montserrat'] ${
                                        filterStatus === status
                                            ? 'bg-[#FF6B35] text-white border border-[#FF6B35]'
                                            : 'bg-transparent text-[#A3A3A3] border border-[#2A2A2A] hover:border-[#FF6B35]'
                                    }`}
                                >
                                    {status === 'all' ? 'All' : status}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Orders List */}
                    <div className="space-y-4">
                        {filteredOrders.map((order) => {
                            const StatusIcon = statusConfig[order.status].icon;
                            return (
                                <div key={order.id} className="bg-[#1A1A1A] border border-[#2A2A2A] p-4 md:p-6 hover:border-[#FF6B35]/30 transition-colors">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                                        <div>
                                            <p className="text-xs text-[#525252] font-['Montserrat'] mb-1">Order #{order.id}</p>
                                            <p className="text-xs text-[#A3A3A3] font-['Montserrat']">Placed on {new Date(order.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <StatusIcon className="w-4 h-4" style={{ color: statusConfig[order.status].color }} />
                                            <span className="text-xs font-semibold uppercase tracking-wider font-['Montserrat']" style={{ color: statusConfig[order.status].color }}>
                                                {statusConfig[order.status].label}
                                            </span>
                                        </div>
                                    </div>

                                    {order.items.map((item, i) => (
                                        <div key={i} className="flex items-center gap-4 py-3 border-t border-[#2A2A2A]">
                                            <img src={item.image} alt={item.name} className="w-14 h-18 md:w-16 md:h-20 object-cover" />
                                            <div className="flex-1">
                                                <h3 className="text-sm font-medium text-white font-['Montserrat']">{item.name}</h3>
                                                <p className="text-xs text-[#525252] font-['Montserrat']">Qty: {item.quantity}</p>
                                            </div>
                                        </div>
                                    ))}

                                    <div className="flex items-center justify-between pt-3 border-t border-[#2A2A2A] mt-3">
                                        <p className="text-sm font-bold text-[#FF6B35] font-['Montserrat']">
                                            Total: {'\u20B9'}{order.total.toLocaleString('en-IN')}
                                        </p>
                                        <button className="inline-flex items-center gap-1 text-xs font-medium text-[#A3A3A3] hover:text-[#FF6B35] transition-colors font-['Montserrat'] uppercase tracking-wider">
                                            View Details <ChevronRight className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        {filteredOrders.length === 0 && (
                            <div className="text-center py-16">
                                <Package className="w-12 h-12 text-[#2A2A2A] mx-auto mb-4" />
                                <p className="text-base text-[#A3A3A3] mb-2 font-['Montserrat']">No orders found</p>
                                <p className="text-sm text-[#525252] font-['Montserrat']">Try adjusting your search or filters</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Orders;
